import { FC, useCallback, useState } from 'react';

import { toSavedPhone } from '../../../helpers/phone';
import { useAuth, useFlow } from '../../../providers';
import { useUpdateAccountMutation } from '../../../types/generated';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { PhoneFormContainer } from './PhoneFormContainer';

interface Props {
  experimentalProductSmsCapture?: boolean;
  image?: string;
  phone?: string;
}

export const PhoneContainer: FC<Props> = ({
  experimentalProductSmsCapture = false,
  image,
  phone = '',
}) => {
  const { currentStepId, goNextStep } = useFlow();
  const { updateUser, user } = useAuth();
  const [updateAccount, { error, loading }] = useUpdateAccountMutation();
  const [emitter] = useAnalyticsEmitter();
  const [submitting, setSubmitting] = useState(false);

  const handleSkip = useCallback(() => {
    emitter('', 'skip clicked');
    goNextStep();
  }, [emitter, goNextStep]);

  const handleSubmit = useCallback(
    async (data: { phone: string }) => {
      setSubmitting(true);
      const res = await updateAccount({
        variables: {
          phone: toSavedPhone(data.phone),
          // Stamp the opt-in time only the first time a phone is saved, so we
          // don't overwrite the original opt-in on subsequent phone updates.
          phoneOptedInAt: user?.phoneOptedInAt
            ? undefined
            : new Date().toISOString(),
        },
      });
      if (res.errors) {
        setSubmitting(false);
        return;
      }
      // Sync the freshly-saved account (incl. phoneOptedInAt) into auth state so
      // a later subscribe in the same session doesn't re-prompt the phone step.
      if (res.data?.updateAccount) {
        updateUser(res.data.updateAccount);
      }
      emitter('', 'save clicked');
      // Hold the disabled state briefly so the button doesn't flicker back
      // to enabled before the dialog closes. submitting is intentionally
      // left true — the component unmounts when the flow closes.
      await new Promise((resolve) => setTimeout(resolve, 500));
      goNextStep();
    },
    [emitter, goNextStep, updateAccount, updateUser, user],
  );

  if (currentStepId !== 'phone') {
    return null;
  }

  return (
    <PhoneFormContainer
      error={Boolean(error)}
      experimentalProductSmsCapture={experimentalProductSmsCapture}
      imageUrl={image}
      loading={loading || submitting}
      onSkip={handleSkip}
      onSubmit={handleSubmit}
      phone={phone}
    />
  );
};
